import type {
  AlgoliaFacet,
  JobAdFacetPropertiesFromAlgolia,
  JobAdFacetValuesFromAlgolia,
} from "@/modules/jobAds/types/jobAdFromAlgolia"
import type { CurrentUserCurriculumIds, CurrentUserSearchCriteria, SearchConfigFilter } from "@/modules/user/api/types"
import type { MultipleQueriesQuery } from "@algolia/client-search"

import _cloneDeep from "lodash/cloneDeep"
import _omit from "lodash/omit"
import _snakeCase from "lodash/snakeCase"
import { NIL as NIL_UUID } from "uuid"

import {
  ALGOLIA_ATTRIBUTES_TO_RETRIEVE,
  ALGOLIA_FACETS,
  DEFAULT_RADIUS_KM,
  LOCALE_NONE,
  PUBLICATION_TYPES,
  RESULTS_SORT_OPTIONS_MAPPING,
} from "@/modules/jobAds/client/constants"
import { computeAlgoliaOptionalFilters } from "@/modules/jobAds/client/utils/computeAlgoliaOptionalFilters"
import { computeAvailableSearchConfigOptions } from "@/modules/jobAds/client/utils/computeAvailableSearchConfigOptions"
import { getEscapedAlgoliaValue } from "@/modules/jobAds/client/utils/getEscapedAlgoliaValue"
import { computeLocationObjectFromSingleQueryParam, getLocationType } from "@/modules/locations/locations"
import { logger } from "@/modules/monitoring/logger"

export type AlgoliaJobAdsRequestParameters = {
  indexName: string
  isLogged: boolean
  location: string | null
  lat: string | null
  lng: string | null
  radius?: number
  isAbroadOnly: boolean
  userCountry: string | undefined | null
  workExperiences?: string[]
  remoteTypes?: string[]
  facultySchoolId?: string | undefined
  companyBusinessTypes?: string[]
  candidacyTypes?: string[]
  ccPositionCategoryIds?: string[]
  curriculumIds: CurrentUserCurriculumIds
  currentUserSearchCriteria: CurrentUserSearchCriteria | undefined
  schoolId: string
  locales: string[]
  page: number
  contractTypes: string[]
  positionCategories: string[]
  userQuery: string
  contractTypesSearchConfig: SearchConfigFilter | undefined
  studyLevelsSearchConfig?: SearchConfigFilter | undefined
  contractDurations?: string[]
  studyLevels?: string[]
  startDates?: string[]
  tags?: string[]
  exclusive: string[]
  excludedStudyLevels?: number[]
  companySectors?: string[]
  companyIds?: string[]
  ccShortName?: string
}

export type AlgoliaJobAdsRequest = {
  mainQuery: MultipleQueriesQuery
  sponsoredQuery: MultipleQueriesQuery
  facetQueries: MultipleQueriesQuery[]
}

export const AND_OPERATOR = "AND"
export const OR_OPERATOR = "OR"
const NOT_OPERATOR = "NOT"
const NOT_OPERATOR_SUBSTITUTE = "-"

// To be able to create OR between cc network facets values
// (EXCLUSIVE, PARTNER_SCHOOL_IDS, CREATED_BY_STUDENT_OR_ALUMNI)
// we collect these values in a new common field CC_NETWORK
export const ccNetworkFilters = ["EXCLUSIVE", "PARTNER_SCHOOL_IDS", "CREATED_BY_STUDENT_OR_ALUMNI"] as const
export type CCNetworkFilters = (typeof ccNetworkFilters)[number]

export type FacetsWithValues = {
  COMPANY_SECTORS: string[]
  CONTRACT_DURATION: string[]
  CONTRACT_TYPE: string[]
  CURRICULUM_IDS: string[]
  LOCALE: string[]
  POSITION_CATEGORY_UUID: string[]
  START_DATE: string[]
  STUDY_LEVELS: string[]
  EXCLUDED_STUDY_LEVELS: string[][]
  SCHOOL_IDS: string[]
  CC_NETWORK: string[]
  TAGS: string[][]
  LOCATIONS: string[]
  SCORE: string[]
  WORK_EXPERIENCE_CODE: string[]
  REMOTE_TYPES: string[]
  COMPANY_BUSINESS_TYPE: string[]
  CANDIDACY_TYPE: string[]
  COMPANY_IDS: string[]
}

const createInitialFacetsFilters = (): FacetsWithValues => ({
  CANDIDACY_TYPE: [],
  CC_NETWORK: [],
  COMPANY_BUSINESS_TYPE: [],
  COMPANY_IDS: [],
  COMPANY_SECTORS: [],
  CONTRACT_DURATION: [],
  CONTRACT_TYPE: [],
  CURRICULUM_IDS: [],
  EXCLUDED_STUDY_LEVELS: [],
  LOCALE: [],
  LOCATIONS: [],
  POSITION_CATEGORY_UUID: [],
  REMOTE_TYPES: [],
  SCHOOL_IDS: [],
  SCORE: [],
  START_DATE: [],
  STUDY_LEVELS: [],
  TAGS: [],
  WORK_EXPERIENCE_CODE: [],
})

export const getFacetFiltersFromParams = (
  params: AlgoliaJobAdsRequestParameters,
  userCountry: string | null | undefined
): FacetsWithValues => {
  const facetsFilters = createInitialFacetsFilters()
  const {
    companySectors,
    contractDurations,
    contractTypes,
    curriculumIds,
    exclusive,
    locales,
    positionCategories,
    facultySchoolId,
    schoolId,
    startDates,
    studyLevels,
    tags,
    location,
    isAbroadOnly,
    workExperiences,
    remoteTypes,
    companyBusinessTypes,
    candidacyTypes,
    excludedStudyLevels,
    companyIds,
  } = params

  if (location) {
    const locationObject = computeLocationObjectFromSingleQueryParam(location)
    const locationType = getLocationType(locationObject)
    const hasLocationTextFilter = locationType !== "city"
    const locationValue = locationObject[locationType]
    if (hasLocationTextFilter && locationValue) {
      facetsFilters.LOCATIONS = [
        `${ALGOLIA_FACETS.LOCATIONS.replace("<location_type>", _snakeCase(locationType))}:'${getEscapedAlgoliaValue(
          locationValue
        )}'`,
      ]
    }
  }

  if (isAbroadOnly) {
    if (userCountry) {
      facetsFilters.LOCATIONS = [
        `${NOT_OPERATOR_SUBSTITUTE}${ALGOLIA_FACETS.LOCATIONS.replace("<location_type>", "country")}:'${userCountry}'`,
      ]
    } else {
      logger.error(
        "Trying to compute a Algolia request with abroad only filter active but unable to find the user's country."
      )
    }
  }

  if (candidacyTypes && candidacyTypes.length > 0) {
    facetsFilters.CANDIDACY_TYPE = candidacyTypes.map(
      candidacyType => `${ALGOLIA_FACETS.CANDIDACY_TYPE}:'${candidacyType}'`
    )
  }

  if (companySectors && companySectors.length > 0) {
    facetsFilters.COMPANY_SECTORS = companySectors.map(
      companySector => `${ALGOLIA_FACETS.COMPANY_SECTORS}:'${companySector}'`
    )
  }
  if (contractDurations && contractDurations.length > 0) {
    facetsFilters.CONTRACT_DURATION = contractDurations.map(
      duration => `${ALGOLIA_FACETS.CONTRACT_DURATION}:${duration}`
    )
  }
  if (contractTypes.length > 0) {
    facetsFilters.CONTRACT_TYPE = contractTypes.map(contractType => `${ALGOLIA_FACETS.CONTRACT_TYPE}:'${contractType}'`)
  }
  if (curriculumIds && curriculumIds.length > 0) {
    facetsFilters.CURRICULUM_IDS = curriculumIds.map(curriculum => `${ALGOLIA_FACETS.CURRICULUM_IDS}:'${curriculum}'`)
  }
  if (locales.length > 0) {
    facetsFilters.LOCALE = locales.map(locale => `${ALGOLIA_FACETS.LOCALE}:'${locale}'`)
  }
  if (positionCategories.length > 0) {
    facetsFilters.POSITION_CATEGORY_UUID = positionCategories.map(
      category => `${ALGOLIA_FACETS.POSITION_CATEGORY_UUID}:'${category}'`
    )
  }
  if (startDates && startDates.length > 0) {
    facetsFilters.START_DATE = startDates.map(startDate => `${ALGOLIA_FACETS.START_DATE}:'${startDate}'`)
  }
  if (studyLevels && studyLevels.length > 0) {
    facetsFilters.STUDY_LEVELS = studyLevels.map(studyLevel => `${ALGOLIA_FACETS.STUDY_LEVELS}:'${studyLevel}'`)
  }

  if (excludedStudyLevels && excludedStudyLevels.length > 0) {
    facetsFilters.EXCLUDED_STUDY_LEVELS = excludedStudyLevels.map(excludedStudyLevel => [
      `${NOT_OPERATOR} ${ALGOLIA_FACETS.STUDY_LEVELS}:${excludedStudyLevel}`,
    ])
  }

  // CC specific
  if (schoolId !== NIL_UUID) {
    facetsFilters.SCHOOL_IDS = [`${ALGOLIA_FACETS.SCHOOL_IDS}:'${schoolId}'`]

    if (facultySchoolId && facultySchoolId !== "") {
      facetsFilters.SCHOOL_IDS.push(`${ALGOLIA_FACETS.SCHOOL_IDS}:'${facultySchoolId}'`)
    }

    if (exclusive.length > 0) {
      exclusive.forEach(exclusiveFilter => {
        switch (exclusiveFilter) {
          case "reserved":
            facetsFilters.CC_NETWORK.push(`${ALGOLIA_FACETS.EXCLUSIVE}:true`)
            break
          case "partner_faculty_career_center":
            if (facultySchoolId !== NIL_UUID) {
              facetsFilters.CC_NETWORK.push(`${ALGOLIA_FACETS.PARTNER_SCHOOL_IDS}:'${facultySchoolId}'`)
            }
            break
          case "partner_career_center":
            facetsFilters.CC_NETWORK.push(`${ALGOLIA_FACETS.PARTNER_SCHOOL_IDS}:'${schoolId}'`)
            break
          case "created_by_student_or_alumni":
            facetsFilters.CC_NETWORK.push(`${ALGOLIA_FACETS.CREATED_BY_STUDENT_OR_ALUMNI}:true`)
            break
          default:
            break
        }
      })
    }
    if (tags && tags.length > 0) {
      facetsFilters.TAGS = tags.map(tag => {
        const schoolTagFilter = `${ALGOLIA_FACETS.TAGS.replace("<school_uuid>", schoolId)}:'${getEscapedAlgoliaValue(
          tag
        )}'`

        const facultyTagFilter =
          facultySchoolId && facultySchoolId !== ""
            ? `${ALGOLIA_FACETS.TAGS.replace("<school_uuid>", facultySchoolId)}:'${getEscapedAlgoliaValue(tag)}'`
            : ""

        return [schoolTagFilter, facultyTagFilter].filter(Boolean)
      })
    }
  }

  if (workExperiences && workExperiences.length > 0) {
    facetsFilters.WORK_EXPERIENCE_CODE = workExperiences.map(
      workExperience => `${ALGOLIA_FACETS.WORK_EXPERIENCE_CODE}:'${workExperience}'`
    )
  }

  if (remoteTypes && remoteTypes.length > 0) {
    facetsFilters.REMOTE_TYPES = remoteTypes.map(remoteType => `${ALGOLIA_FACETS.REMOTE_TYPES}:'${remoteType}'`)
  }

  if (companyBusinessTypes && companyBusinessTypes.length > 0) {
    facetsFilters.COMPANY_BUSINESS_TYPE = companyBusinessTypes.map(
      companyBusinessType => `${ALGOLIA_FACETS.COMPANY_BUSINESS_TYPE}:'${companyBusinessType}'`
    )
  }

  if (companyIds && companyIds.length > 0) {
    facetsFilters.COMPANY_IDS = companyIds.map(companyId => `${ALGOLIA_FACETS.COMPANY_IDS}:'${companyId}'`)
  }

  return facetsFilters
}

export const getLatLngParam = (
  lat: string | null,
  lng: string | null,
  radius: number | undefined
): { aroundLatLng?: string; aroundRadius?: number } => {
  if (!lat || !lng) return {}

  return {
    aroundLatLng: `${lat}, ${lng}`,
    aroundRadius: (radius || DEFAULT_RADIUS_KM) * 1000,
  }
}

export const addFacetFiltersFromBusinessRules = (
  initialFacetFilters: FacetsWithValues,
  initialParams: AlgoliaJobAdsRequestParameters
): FacetsWithValues => {
  const { contractTypesSearchConfig, studyLevelsSearchConfig } = initialParams
  const result: FacetsWithValues = _cloneDeep(initialFacetFilters)

  const allAvailableContractTypeValues = contractTypesSearchConfig
    ? computeAvailableSearchConfigOptions(contractTypesSearchConfig)
    : []
  const allAvailableStudyLevelValues = studyLevelsSearchConfig
    ? computeAvailableSearchConfigOptions(studyLevelsSearchConfig)
    : []

  // If no selected contract types from user, we need to add all contract types from current CC
  if (initialFacetFilters.CONTRACT_TYPE.length === 0) {
    result.CONTRACT_TYPE = allAvailableContractTypeValues.map(value => `${ALGOLIA_FACETS.CONTRACT_TYPE}:'${value}'`)
  }
  // If no selected study levels from user, we need to add all study levels from current CC
  if (initialFacetFilters.STUDY_LEVELS.length === 0) {
    result.STUDY_LEVELS = allAvailableStudyLevelValues.map(value => `${ALGOLIA_FACETS.STUDY_LEVELS}:'${value}'`)
  }
  // We always want to see job ad that are not curriculum related
  result.CURRICULUM_IDS.push(`${ALGOLIA_FACETS.CURRICULUM_IDS}:'${NIL_UUID}'`)

  // We always want to be able to see public (www) job ads
  result.SCHOOL_IDS.push(`${ALGOLIA_FACETS.SCHOOL_IDS}:'${NIL_UUID}'`)

  return result
}

export const enabledSponsoredFiltersRules = (facetFilters: string): string => {
  return `${facetFilters} ${AND_OPERATOR} (${ALGOLIA_FACETS.SCORE}:${PUBLICATION_TYPES.FLASH_OR_SPONSORED})`
}

const formatFilterValues = (facet: string[]): string => {
  if (facet.length > 1) {
    return `( ${facet.join(` ${OR_OPERATOR} `)} )`
  }
  const singleFacet = facet[0]
  if (singleFacet.charAt(0) === NOT_OPERATOR_SUBSTITUTE) {
    return singleFacet.replace(NOT_OPERATOR_SUBSTITUTE, `${NOT_OPERATOR} `)
  }
  return singleFacet
}

export const formatFiltersForAlgolia = (filters: FacetsWithValues, excludedFacet?: keyof FacetsWithValues): string => {
  let filtersToFormat: Partial<FacetsWithValues> = _cloneDeep(filters)
  if (excludedFacet !== undefined) {
    const { [excludedFacet]: _, ...restFilters } = filters
    filtersToFormat = restFilters
  }

  const filtersValues: string[][] = []
  const is2DArray = (facet: string[] | string[][]): facet is string[][] => facet.every(item => Array.isArray(item))

  Object.values(filtersToFormat)
    .filter(facet => facet.length > 0)
    .forEach(facet => {
      if (is2DArray(facet)) {
        facet.forEach(item => filtersValues.push(item))
      } else {
        filtersValues.push(facet)
      }
    })

  return filtersValues
    .map(facet => [...new Set(facet)])
    .map(formatFilterValues)
    .join(` ${AND_OPERATOR} `)
}

export const RULE_CONTEXTS = {
  LOGGING_STATUS: "is_logged",
  USER_COUNTRY: "user_country",
} as const

export type GetRuleContextsParams = {
  isLogged: boolean
  userCountry: string | null | undefined
  ccShortName: string | null | undefined
}
export const getRuleContexts = (params: GetRuleContextsParams): string[] => {
  const { isLogged = false, userCountry = "", ccShortName } = params

  const userContext: string[] = []

  // Logging status
  userContext.push(`${RULE_CONTEXTS.LOGGING_STATUS}__${isLogged}`)

  // User country
  if (userCountry !== null && userCountry !== "") {
    userContext.push(`${RULE_CONTEXTS.USER_COUNTRY}__${userCountry}`)
  }

  // CC shortName (fallback to WWW if needed)
  userContext.push(`permalink__${ccShortName?.replaceAll(" ", "-").toLowerCase() || "www"}`)

  return userContext
}

const computeFacetFiltersForFacetQuery = (facetFilters: FacetsWithValues, facetName: AlgoliaFacet): string => {
  if (facetName === "EXCLUSIVE" || facetName === "CREATED_BY_STUDENT_OR_ALUMNI" || facetName === "PARTNER_SCHOOL_IDS") {
    return formatFiltersForAlgolia(facetFilters, "CC_NETWORK")
  }
  if (facetName === "TAGS") {
    return formatFiltersForAlgolia(facetFilters)
  }
  return formatFiltersForAlgolia(facetFilters, facetName)
}

export const formatAlgoliaFacetValue = (
  algoliaFacetProperty: JobAdFacetPropertiesFromAlgolia,
  algoliaFacetValue: JobAdFacetValuesFromAlgolia,
  facultySchoolId: string | undefined,
  schoolId: string
  // eslint-disable-next-line max-params
): string[] => {
  if (algoliaFacetProperty === "TAGS") {
    return [
      algoliaFacetValue.replace("<school_uuid>", schoolId),
      ...(facultySchoolId && facultySchoolId !== ""
        ? [algoliaFacetValue.replace("<school_uuid>", facultySchoolId)]
        : []),
    ]
  }

  return [algoliaFacetValue]
}

export const computeAlgoliaJobAdsRequest = (params: AlgoliaJobAdsRequestParameters): AlgoliaJobAdsRequest => {
  const {
    indexName = process.env.NEXT_PUBLIC_ALGOLIA_JOB_ADS_INDEX_NAME,
    userQuery,
    page,
    location,
    lat,
    lng,
    radius,
    facultySchoolId,
    schoolId,
    isLogged,
    userCountry,
    locales,
    ccShortName,
  } = params

  const locationObject = location ? computeLocationObjectFromSingleQueryParam(location) : null
  const locationType = locationObject ? getLocationType(locationObject) : null

  const cleanLocales = (locales || []).filter(locale => locale !== LOCALE_NONE)
  const cleanParams = {
    ...params,
    locales: cleanLocales,
  }

  const facetFilters = addFacetFiltersFromBusinessRules(
    getFacetFiltersFromParams(cleanParams, userCountry),
    cleanParams
  )

  const isOptionalFilterEnabled = indexName !== RESULTS_SORT_OPTIONS_MAPPING.RECENCY

  const optionalFilters = isOptionalFilterEnabled ? computeAlgoliaOptionalFilters(params, facetFilters) : null

  const filters = formatFiltersForAlgolia(facetFilters)

  const aroundLatLngParams = getLatLngParam(lat, lng, radius)
  const ruleContexts = getRuleContexts({ ccShortName, isLogged, userCountry })

  const sponsoredFilters = enabledSponsoredFiltersRules(filters)

  const commonParams = {
    analytics: true,
    attributesToHighlight: [],
    attributesToRetrieve: ALGOLIA_ATTRIBUTES_TO_RETRIEVE,
    clickAnalytics: true,
    facets: [],
    page,
    ...(locationType === "city" && { ...aroundLatLngParams }),
    ...(optionalFilters && { optionalFilters }),
  }

  const sponsoredQuery = {
    indexName: process.env.NEXT_PUBLIC_ALGOLIA_JOB_ADS_OF_THE_WEEK_INDEX_NAME,
    params: {
      ...commonParams,
      filters: sponsoredFilters,
      hitsPerPage: 2,
      ruleContexts,
    },
    query: userQuery,
  }

  const mainQuery = {
    indexName,
    params: {
      ...commonParams,
      filters,
      hitsPerPage: 20,
      ruleContexts,
    },
    query: userQuery,
  }

  const facetQueries = (
    Object.entries(ALGOLIA_FACETS) as [JobAdFacetPropertiesFromAlgolia, JobAdFacetValuesFromAlgolia][]
  )
    .filter(([facetKey]) => facetKey !== "LOCATIONS")
    .map(([algoliaFacetProperty, algoliaFacetValue]) => {
      const facetFiltersForFacetQuery = computeFacetFiltersForFacetQuery(facetFilters, algoliaFacetProperty)

      return {
        indexName,
        params: {
          attributesToHighlight: [],
          attributesToRetrieve: ALGOLIA_ATTRIBUTES_TO_RETRIEVE,
          facets: formatAlgoliaFacetValue(algoliaFacetProperty, algoliaFacetValue, facultySchoolId, schoolId),
          filters: facetFiltersForFacetQuery,
          hitsPerPage: 0,
          ...(locationType === "city" && { ...aroundLatLngParams }),
        },
        query: userQuery,
      }
    })

  return {
    facetQueries,
    mainQuery,
    sponsoredQuery,
  }
}
